// ============================================
// AnnouncementModal — "what's new" overlay backed by Supabase
//
// Renders the highest-version active announcement from
// public.app_announcements once per user, per version. Mounted in
// Layout when a session exists. Dismissal (X or CTA tap) writes the
// version to localStorage so the modal doesn't re-fire next launch.
// ============================================

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useCloudQuery } from '../../data/hooks';
import { fetchActiveAnnouncement, type Announcement } from '../../data/cloud';
import { trackEvent } from '../../utils/analytics';
import './AnnouncementModal.css';

const SEEN_KEY = 'prtcl_announcement_version_seen';

export function getSeenVersion(storage: Pick<Storage, 'getItem'> = localStorage): number {
  try {
    const raw = storage.getItem(SEEN_KEY);
    const n = raw !== null ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  } catch { return 0; }
}

function markSeen(version: number) {
  try { localStorage.setItem(SEEN_KEY, String(version)); } catch {}
}

/**
 * Classifies a cta_path string. Internal paths must start with '/'
 * and contain no protocol-relative '//' or colons (rules out
 * javascript:, data:, file:, etc.). External paths must start with
 * https:// (not http:// — avoid mixed-content gotchas).
 *
 * Exported for unit testing.
 */
export function classifyCtaPath(path: string): 'internal' | 'external' | 'invalid' {
  if (/^https:\/\//.test(path)) return 'external';
  if (path.startsWith('/') && !path.startsWith('//') && !path.includes(':')) return 'internal';
  return 'invalid';
}

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

export default function AnnouncementModal() {
  const navigate = useNavigate();
  // Initial value 'undefined' lets the cloud query distinguish loading vs.
  // "no announcement on the server." Null = loaded, none active.
  const { data: announcement } = useCloudQuery<Announcement | null>(
    'app_announcements',
    fetchActiveAnnouncement,
    null,
  );
  const [dismissed, setDismissed] = useState(false);

  // Track seen version locally so subsequent renders within this session
  // don't re-show the modal after the user dismisses it.
  const [seenVersion, setSeenVersion] = useState(() => getSeenVersion());

  const isVisible =
    !!announcement &&
    !dismissed &&
    announcement.version > seenVersion;

  // Mark seen + close, WITHOUT firing the dismissed-by-user event. Used
  // by both explicit dismiss and CTA tap; only the dismiss path adds
  // the `announcement_dismissed` event.
  const closeAndMarkSeen = useCallback(() => {
    if (announcement) {
      markSeen(announcement.version);
      setSeenVersion(announcement.version);
    }
    setDismissed(true);
  }, [announcement]);

  const handleDismiss = useCallback(() => {
    if (announcement) {
      trackEvent('announcement_dismissed', { version: announcement.version });
    }
    closeAndMarkSeen();
  }, [announcement, closeAndMarkSeen]);

  const handleCta = useCallback(() => {
    if (!announcement || !announcement.ctaPath) return;
    const path = announcement.ctaPath;
    const kind = classifyCtaPath(path);
    if (kind === 'invalid') {
      Sentry.captureException(
        new Error(`AnnouncementModal: rejected unsafe cta_path "${path}" on version ${announcement.version}`)
      );
      closeAndMarkSeen();
      return;
    }
    trackEvent('announcement_cta_clicked', { version: announcement.version, cta_path: path });
    closeAndMarkSeen();
    if (kind === 'external') {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      navigate(path);
    }
  }, [announcement, closeAndMarkSeen, navigate]);

  // Lock background scroll while open + fire "viewed" analytics once
  // per render of a new announcement.
  useEffect(() => {
    if (!isVisible || !announcement) return;
    trackEvent('announcement_viewed', {
      version: announcement.version,
      title: announcement.title,
    });
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isVisible, announcement]);

  if (!isVisible) return null;

  const { eyebrow, title, body, imageUrl, ctaLabel } = announcement!;

  return (
    <div className="ann-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="ann-backdrop" onClick={handleDismiss} />
      <div className="ann-sheet">
        <button className="ann-close" onClick={handleDismiss} aria-label="Dismiss">
          <CloseIcon />
        </button>
        {imageUrl && (
          <div className="ann-image">
            <img src={imageUrl} alt="" />
          </div>
        )}
        <span className="ann-eyebrow">{eyebrow}</span>
        <h2 className="ann-title">{title}</h2>
        {body && <p className="ann-body">{body}</p>}
        {ctaLabel && (
          <button className="ann-cta" onClick={handleCta}>
            <span>{ctaLabel}</span>
            <ArrowIcon />
          </button>
        )}
      </div>
    </div>
  );
}
